import { ApolloQueryResult } from '@apollo/client';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { AssessmentAnswer } from '../../../../content/assessment/utils/assessmentUtils';
import {
  SurveyAnalyticsConsentStatusQuery,
  useActiveSurveyDefinitionsQuery,
  useSurveyAnalyticsConsentStatusQuery,
} from '../../../../graphQL';
import { Setter } from '../../../../types/reactTypes';
import Sentry from '../../../../utils/sentry';
import {
  SurveyQuestionnaireKeyMapping,
  UNVERSIONED_QUESTIONNAIRE_KEY_TO_ROUTE,
} from '../surveyRoutes';

type QuestionnaireKey = string;
type QuestionKey = string;

type Answers = Record<QuestionKey, AssessmentAnswer>;
type AnswersByQuestionnaire = Record<QuestionnaireKey, Answers>;
type AnswerAction = {
  answers: Answers;
  questionnaireKey?: string;
  type: 'set' | 'reset';
};

type SurveyContextType = {
  answersByQuestionnaire: AnswersByQuestionnaire;
  questionnaireMapping: SurveyQuestionnaireKeyMapping;
  activeSurveyId: number;
  hasStarted: boolean;
  hasSignedConsent: boolean; // no setter, handled internally
  loading: boolean;
  error: string | null;
  setAnswer: (questionnaireKey: string, questionKey: string, answer: AssessmentAnswer) => void;
  resetAnswers: () => void;
  setHasStarted: Setter<boolean>;
  refetchConsent: () =>
    | Promise<ApolloQueryResult<SurveyAnalyticsConsentStatusQuery>>
    | Promise<void>;
};

const SurveyContext = createContext<SurveyContextType | null>(null);

export const SurveyConsumer = SurveyContext.Consumer;

const createQuestionnaireMapping = (
  questionnaireKeys: string[],
  questionnaireVersionDelimiter: string,
): SurveyQuestionnaireKeyMapping => {
  const mapping: Partial<SurveyQuestionnaireKeyMapping> = {};

  // Remove version suffix using the provided delimiter (e.g., "--v" + digits)
  const escapedDelimiter = questionnaireVersionDelimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const versionRegex = new RegExp(`${escapedDelimiter}\\d+$`);

  for (const questionnaireKey of questionnaireKeys) {
    const baseKey = questionnaireKey.replace(versionRegex, '');
    const routeKey = UNVERSIONED_QUESTIONNAIRE_KEY_TO_ROUTE[baseKey];

    if (routeKey !== undefined) {
      mapping[routeKey] = questionnaireKey;
    } else {
      Sentry.captureException(new Error('No route mapping found for questionnaire key'), {
        extra: { questionnaireKey, questionnaireVersionDelimiter },
      });
    }
  }

  // This assertion is safe because we control the mapping and validate the enum values
  return mapping as SurveyQuestionnaireKeyMapping;
};

// Helper function to create error state provider values
const createErrorProviderValue = (
  error: string,
  questionnaireMapping?: SurveyQuestionnaireKeyMapping,
): SurveyContextType => ({
  answersByQuestionnaire: {},
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  questionnaireMapping: questionnaireMapping ?? ({} as SurveyQuestionnaireKeyMapping),
  activeSurveyId: 0,
  hasStarted: false,
  hasSignedConsent: false,
  loading: false,
  error,
  setAnswer: () => undefined,
  resetAnswers: () => undefined,
  setHasStarted: () => undefined,
  refetchConsent: async () => undefined,
});

export const SurveyProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [hasStarted, setHasStarted] = useState(false);
  const [hasSignedConsent, setHasSignedConsent] = useState(false);
  const [activeSurveyId, setActiveSurveyId] = useState<number>(0);
  const { data, loading: defsLoading, error: queryError } = useActiveSurveyDefinitionsQuery();

  // Load consent status once we know surveyId
  const {
    data: consentData,
    loading: consentLoading,
    refetch: refetchConsent,
  } = useSurveyAnalyticsConsentStatusQuery({
    skip: !activeSurveyId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const serverHasConsent =
      consentData?.surveyAnalyticsConsentStatus?.hasSignedSurveyAnalyticsConsent ?? false;
    setHasSignedConsent(serverHasConsent);
  }, [consentData?.surveyAnalyticsConsentStatus?.hasSignedSurveyAnalyticsConsent]);

  const [answersByQuestionnaire, dispatch] = useReducer(
    (state: AnswersByQuestionnaire, action: AnswerAction) => {
      if (action.type === 'reset') {
        return {};
      }

      if (action.questionnaireKey === undefined) {
        return state;
      }

      const { questionnaireKey } = action;

      return {
        ...state,
        [questionnaireKey]: { ...(state[questionnaireKey] ?? {}), ...action.answers },
      };
    },
    {},
  );

  useEffect(() => {
    if (data) {
      setActiveSurveyId(data.activeSurveyDefinitions.surveyId);
    }
  }, [data?.activeSurveyDefinitions.surveyId]);

  const loading = defsLoading || (activeSurveyId !== 0 && consentLoading);

  const questionnaireMapping = useMemo((): SurveyQuestionnaireKeyMapping | undefined => {
    const questionnaireKeys = data?.activeSurveyDefinitions?.questionnaireKeys;
    const questionnaireVersionDelimiter =
      data?.activeSurveyDefinitions?.questionnaireVersionDelimiter;

    if (
      !questionnaireKeys ||
      questionnaireKeys.length === 0 ||
      questionnaireVersionDelimiter === undefined
    ) {
      return undefined;
    }

    const mapping = createQuestionnaireMapping(questionnaireKeys, questionnaireVersionDelimiter);

    // Validate that we got at least some valid mappings
    if (Object.keys(mapping).length === 0) {
      Sentry.captureException(new Error('No valid questionnaire mappings created from keys'));
      return undefined;
    }

    return mapping;
  }, [
    data?.activeSurveyDefinitions?.questionnaireKeys,
    data?.activeSurveyDefinitions?.questionnaireVersionDelimiter,
  ]);

  const setAnswer = (
    questionnaireKey: string,
    questionKey: string,
    answer: AssessmentAnswer,
  ): void => {
    dispatch({
      answers: {
        [questionKey]: answer,
      },
      questionnaireKey,
      type: 'set',
    });
  };

  const resetAnswers = (): void => {
    dispatch({ answers: {}, type: 'reset' });
  };

  // Helper function to determine provider value based on current state
  const getProviderValue = (): SurveyContextType => {
    // Check for loading state first
    if (loading) {
      return {
        answersByQuestionnaire: {},
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        questionnaireMapping: {} as SurveyQuestionnaireKeyMapping,
        activeSurveyId: 0,
        hasStarted: false,
        hasSignedConsent: false,
        loading: true,
        error: null,
        setAnswer: () => undefined,
        resetAnswers: () => undefined,
        setHasStarted: () => undefined,
        refetchConsent: async () => undefined,
      };
    }

    // Check for various error conditions
    if (questionnaireMapping === undefined) {
      return createErrorProviderValue(
        'Failed to process survey questionnaire mapping. The survey configuration may be invalid.',
      );
    }

    if (queryError) {
      return createErrorProviderValue(`Failed to load survey definitions: ${queryError.message}`);
    }

    const activeSurveyDefinitions = data?.activeSurveyDefinitions;
    if (!activeSurveyDefinitions) {
      return createErrorProviderValue('No active survey definitions found for organization');
    }

    if (activeSurveyId === undefined) {
      return createErrorProviderValue(
        'No active survey id found for organization',
        questionnaireMapping,
      );
    }

    // Success case - return fully populated context
    return {
      answersByQuestionnaire,
      questionnaireMapping,
      activeSurveyId,
      hasStarted,
      hasSignedConsent,
      error: null,
      loading: false,
      setAnswer,
      resetAnswers,
      setHasStarted,
      refetchConsent,
    };
  };

  const providerValue = getProviderValue();

  return <SurveyContext.Provider value={providerValue}>{children}</SurveyContext.Provider>;
};

export const useSurveyContext = (): SurveyContextType => {
  const context = useContext(SurveyContext);
  if (context === null) {
    throw new Error(
      'useSurveyContext must be used within a SurveyProvider. ' +
        'Make sure your component is wrapped in a SurveyProvider.',
    );
  }
  return context;
};
